import { Head, router } from "@inertiajs/react"
import { useState } from "react"
import type { SocialPost } from "@/types"
import DashboardLayout from "@/components/dashboard/layout"
import SocialPostDetailDialog from "@/components/dashboard/social-post-detail-dialog"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { track } from "@/lib/analytics"
import { csrfToken } from "@/lib/csrf"

type CalendarPost = SocialPost & { date: string }

import { PLATFORM_LABELS, STATUS_STYLES } from "@/lib/social-post-constants"

const PLATFORM_HEX: Record<string, string> = {
  instagram: "#E1306C",
  facebook: "#1877F2",
  tiktok: "#1A1A1A",
  youtube: "#FF0000",
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

// ---------------------------------------------------------------------------
// Platform social icons (coloured SVGs)
// ---------------------------------------------------------------------------

function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#E1306C">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
    </svg>
  )
}

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1877F2">
      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
    </svg>
  )
}

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#1A1A1A">
      <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
    </svg>
  )
}

function YouTubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="#FF0000">
      <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
  )
}

const PLATFORM_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  instagram: InstagramIcon,
  facebook: FacebookIcon,
  tiktok: TikTokIcon,
  youtube: YouTubeIcon,
}

// ---------------------------------------------------------------------------
// Post kind icons
// ---------------------------------------------------------------------------

function VideoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
    </svg>
  )
}

function ListingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  )
}

function StandaloneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
    </svg>
  )
}

const POST_KIND_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  video: VideoIcon,
  listing: ListingIcon,
  standalone: StandaloneIcon,
}

// ---------------------------------------------------------------------------
// Calendar page
// ---------------------------------------------------------------------------

export default function CalendarIndex({
  posts,
  start_date,
  end_date: _end_date,
  view,
}: {
  posts: CalendarPost[]
  start_date: string
  end_date: string
  view: string
}) {
  const [currentView, setCurrentView] = useState<"month" | "week">(
    view === "month" ? "month" : "week",
  )
  const [selectedPost, setSelectedPost] = useState<CalendarPost | null>(null)

  const startDate = new Date(start_date)
  const currentMonth = startDate.getMonth()
  const currentYear = startDate.getFullYear()

  // Group posts by date
  const postsByDate: Record<string, CalendarPost[]> = {}
  for (const post of posts) {
    const key = post.date
    if (!postsByDate[key]) postsByDate[key] = []
    postsByDate[key].push(post)
  }

  function handleDelete(post: SocialPost) {
    return new Promise<void>((resolve) => {
      router.delete(`/dashboard/posts/${post.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          setSelectedPost(null)
          toast.success("Post deleted")
        },
        onFinish: () => resolve(),
      })
    })
  }

  async function handleRetry(post: SocialPost) {
    try {
      const response = await fetch(`/dashboard/posts/${post.id}/retry`, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken() },
      })

      if (response.ok) {
        track("social_post_retried", { platform: post.platform })
        toast.success("Retrying publish...")
        router.reload()
      } else {
        toast.error("Could not retry")
      }
    } catch {
      toast.error("Something went wrong")
    }
  }

  function navigateTo(start: Date) {
    const end = currentView === "week"
      ? new Date(start.getTime() + 6 * 86400000)
      : new Date(start.getFullYear(), start.getMonth() + 1, 0)

    router.get(
      "/dashboard/calendar",
      {
        start: start.toISOString().slice(0, 10),
        end: end.toISOString().slice(0, 10),
        view: currentView,
      },
      { preserveState: true, preserveScroll: true },
    )
  }

  function handlePrev() {
    if (currentView === "week") {
      navigateTo(new Date(startDate.getTime() - 7 * 86400000))
    } else {
      navigateTo(new Date(currentYear, currentMonth - 1, 1))
    }
  }

  function handleNext() {
    if (currentView === "week") {
      navigateTo(new Date(startDate.getTime() + 7 * 86400000))
    } else {
      navigateTo(new Date(currentYear, currentMonth + 1, 1))
    }
  }

  function handleToday() {
    const today = new Date()
    if (currentView === "week") {
      const day = today.getDay()
      const monday = new Date(today)
      monday.setDate(today.getDate() - ((day + 6) % 7))
      navigateTo(monday)
    } else {
      navigateTo(new Date(today.getFullYear(), today.getMonth(), 1))
    }
  }

  function toggleView() {
    const newView = currentView === "month" ? "week" : "month"
    setCurrentView(newView)
    const start = newView === "week"
      ? (() => {
          const today = new Date()
          const day = today.getDay()
          const monday = new Date(today)
          monday.setDate(today.getDate() - ((day + 6) % 7))
          return monday
        })()
      : new Date(currentYear, currentMonth, 1)

    const end = newView === "week"
      ? new Date(start.getTime() + 6 * 86400000)
      : new Date(start.getFullYear(), start.getMonth() + 1, 0)

    router.get(
      "/dashboard/calendar",
      {
        start: start.toISOString().slice(0, 10),
        end: end.toISOString().slice(0, 10),
        view: newView,
      },
      { preserveState: true, preserveScroll: true },
    )
  }

  // Build calendar grid
  const days = currentView === "month"
    ? buildMonthGrid(currentYear, currentMonth)
    : buildWeekGrid(startDate)

  const title = currentView === "month"
    ? new Date(currentYear, currentMonth).toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    : `${startDate.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${new Date(startDate.getTime() + 6 * 86400000).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`

  return (
    <DashboardLayout>
      <Head title="Calendar | QuiteRight" />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-charcoal">
            Calendar
          </h1>
          <p className="mt-1 text-sm text-warm-grey">
            View your scheduled and published posts.
          </p>
        </div>
      </div>

      {/* Calendar nav */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={handlePrev}>
            <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </Button>
          <h2 className="min-w-[180px] text-center text-sm font-semibold text-charcoal">
            {title}
          </h2>
          <Button variant="secondary" size="sm" onClick={handleNext}>
            <svg className="size-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </Button>
          <Button variant="ghost" size="sm" onClick={handleToday} className="ml-2 text-xs">
            Today
          </Button>
        </div>
        <Button variant="secondary" size="sm" onClick={toggleView} className="text-xs">
          {currentView === "month" ? "Week view" : "Month view"}
        </Button>
      </div>

      {/* Calendar grid */}
      {currentView === "month" ? (
        <div className="overflow-hidden rounded-xl border border-stone/30 bg-white">
          {/* Weekday headers */}
          <div className="grid grid-cols-7 border-b border-stone/20 bg-cream/30">
            {WEEKDAYS.map((wd) => (
              <div key={wd} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-warm-grey">
                {wd}
              </div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7">
            {days.map((day, idx) => {
              const dateStr = day.toISOString().slice(0, 10)
              const dayPosts = postsByDate[dateStr] || []
              const isToday = dateStr === new Date().toISOString().slice(0, 10)
              const isCurrentMonth = day.getMonth() === currentMonth

              return (
                <div
                  key={idx}
                  className={`min-h-[100px] border-b border-r border-stone/15 p-1.5 ${
                    !isCurrentMonth ? "bg-stone/5" : ""
                  }`}
                >
                  <div className={`mb-1 text-right text-xs ${
                    isToday
                      ? "inline-flex size-6 items-center justify-center rounded-full bg-red font-semibold text-white float-right"
                      : isCurrentMonth
                        ? "font-medium text-charcoal"
                        : "text-warm-grey/40"
                  }`}>
                    {day.getDate()}
                  </div>
                  <div className="clear-both space-y-0.5">
                    {dayPosts.slice(0, 3).map((post) => (
                      <PostEntryCompact
                        key={post.id}
                        post={post}
                        onClick={() => setSelectedPost(post)}
                      />
                    ))}
                    {dayPosts.length > 3 && (
                      <span className="block text-center text-[10px] text-warm-grey/50">
                        +{dayPosts.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <WeekView
          days={days}
          postsByDate={postsByDate}
          onSelectPost={setSelectedPost}
        />
      )}

      <SocialPostDetailDialog
        post={selectedPost}
        open={!!selectedPost}
        onOpenChange={(open) => !open && setSelectedPost(null)}
        onDelete={handleDelete}
        onRetry={handleRetry}
        showVideoLink
      />
    </DashboardLayout>
  )
}

// ---------------------------------------------------------------------------
// Week view — vertical layout, one row per day
// ---------------------------------------------------------------------------

function WeekView({
  days,
  postsByDate,
  onSelectPost,
}: {
  days: Date[]
  postsByDate: Record<string, CalendarPost[]>
  onSelectPost: (post: CalendarPost) => void
}) {
  const todayStr = new Date().toISOString().slice(0, 10)

  return (
    <div className="overflow-hidden rounded-xl border border-stone/30 bg-white">
      {days.map((day, idx) => {
        const dateStr = day.toISOString().slice(0, 10)
        const dayPosts = postsByDate[dateStr] || []
        const isToday = dateStr === todayStr

        return (
          <div
            key={idx}
            className="flex border-b border-stone/15 last:border-b-0"
          >
            {/* Day label */}
            <div className={`flex w-24 shrink-0 flex-col items-center justify-start border-r border-stone/15 px-3 py-4 ${
              isToday ? "bg-red/[0.04]" : "bg-cream/30"
            }`}>
              <span className="text-[11px] font-semibold uppercase tracking-wider text-warm-grey">
                {day.toLocaleDateString("en-GB", { weekday: "short" })}
              </span>
              <span className={`mt-1 flex size-8 items-center justify-center rounded-full text-lg font-semibold ${
                isToday
                  ? "bg-red text-white"
                  : "text-charcoal"
              }`}>
                {day.getDate()}
              </span>
              <span className="mt-0.5 text-[10px] text-warm-grey/60">
                {day.toLocaleDateString("en-GB", { month: "short" })}
              </span>
            </div>

            {/* Posts for this day */}
            <div className="min-h-[80px] min-w-0 flex-1 p-3">
              {dayPosts.length === 0 ? (
                <p className="py-4 text-center text-xs text-warm-grey/40">No posts</p>
              ) : (
                <div className="space-y-2">
                  {dayPosts.map((post) => (
                    <WeekPostEntry key={post.id} post={post} onClick={() => onSelectPost(post)} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Detailed post entry for week view
// ---------------------------------------------------------------------------

function WeekPostEntry({
  post,
  onClick,
}: {
  post: CalendarPost
  onClick: () => void
}) {
  const time = post.scheduled_at || post.published_at
  const formattedTime = time
    ? new Date(time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : null

  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-start gap-3 rounded-lg border border-stone/15 bg-cream/20 px-3 py-2.5 text-left transition-colors hover:border-stone/30 hover:bg-stone/10"
    >
      {/* Platform icon */}
      <PlatformIcon platform={post.platform} className="mt-0.5 size-4 shrink-0" />

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-charcoal">
            {PLATFORM_LABELS[post.platform]}
          </span>
          <PostKindIcon kind={post.post_kind} className="size-3 text-warm-grey/60" />
          <span className="text-[10px] capitalize text-warm-grey">
            {post.post_kind || "video"}
          </span>
          <span className={`ml-auto text-[10px] font-medium ${
            post.status === "published"
              ? "text-red"
              : post.status === "failed"
                ? "text-charcoal"
                : post.status === "scheduled"
                  ? "text-charcoal"
                  : "text-warm-grey"
          }`}>
            {STATUS_STYLES[post.status]?.label || post.status}
          </span>
        </div>

        {post.listing_address && (
          <p className="mt-0.5 truncate text-[11px] text-charcoal/70">
            {post.listing_address}
          </p>
        )}

        {post.caption && (
          <p className="mt-0.5 line-clamp-1 text-[11px] text-warm-grey">
            {post.caption}
          </p>
        )}

        {formattedTime && (
          <span className="mt-1 inline-block text-[10px] text-warm-grey/60">
            {formattedTime}
          </span>
        )}
      </div>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Compact post entry for month view
// ---------------------------------------------------------------------------

function PostEntryCompact({
  post,
  onClick,
}: {
  post: CalendarPost
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-1 rounded px-1 py-0.5 text-left text-[10px] leading-tight transition-colors hover:bg-stone/20"
    >
      <PlatformIcon platform={post.platform} className="size-3 shrink-0" />
      <PostKindIcon kind={post.post_kind} className="size-2.5 shrink-0 text-warm-grey/60" />
      <span className="truncate capitalize text-charcoal">
        {post.post_kind === "standalone" ? "Post" : (post.post_kind || "Video")}
      </span>
    </button>
  )
}

// ---------------------------------------------------------------------------
// Icon helpers
// ---------------------------------------------------------------------------

function PlatformIcon({ platform, className }: { platform: string; className?: string }) {
  const Icon = PLATFORM_ICON_MAP[platform]
  if (Icon) return <Icon className={className} />
  return (
    <span
      className={`inline-block shrink-0 rounded-full ${className}`}
      style={{ backgroundColor: PLATFORM_HEX[platform] || "#3A3A3C", width: "0.5rem", height: "0.5rem" }}
    />
  )
}

function PostKindIcon({ kind, className }: { kind?: string; className?: string }) {
  const Icon = POST_KIND_ICONS[kind || "video"]
  if (Icon) return <Icon className={className} />
  return null
}

// ---------------------------------------------------------------------------
// Grid builders
// ---------------------------------------------------------------------------

function buildMonthGrid(year: number, month: number): Date[] {
  const first = new Date(year, month, 1)
  const startDay = (first.getDay() + 6) % 7
  const start = new Date(first)
  start.setDate(start.getDate() - startDay)

  const days: Date[] = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    days.push(d)
  }
  return days
}

function buildWeekGrid(startDate: Date): Date[] {
  const days: Date[] = []
  const day = startDate.getDay()
  const monday = new Date(startDate)
  monday.setDate(startDate.getDate() - ((day + 6) % 7))

  for (let i = 0; i < 7; i++) {
    const d = new Date(monday)
    d.setDate(monday.getDate() + i)
    days.push(d)
  }
  return days
}
